import React, { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send, Loader2 } from "lucide-react";
import api, { API } from "@/lib/api";

function getSessionId() {
  let id = localStorage.getItem("gu_chat_session_id");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("gu_chat_session_id", id);
  }
  return id;
}

const GREETING = "Hi! I'm the Gulfudaan Assistant. Ask me about GAMCA appointments, visas, dummy flight bookings, pricing, or the application process.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [enabled, setEnabled] = useState(null);
  const sessionIdRef = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/chat/config").then((res) => setEnabled(res.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open || messages.length > 0) return;
    api
      .get(`/chat/history/${sessionIdRef.current}`)
      .then((res) => {
        setMessages(res.data.length > 0 ? res.data : [{ role: "assistant", content: GREETING }]);
      })
      .catch(() => setMessages([{ role: "assistant", content: GREETING }]))
      .finally(() => setLoadingHistory(false));
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              assistantText += parsed.delta;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: assistantText };
                return next;
              });
            }
          } catch {
            // ignore malformed chunk
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "Sorry, I'm having trouble responding right now. Please reach us on WhatsApp (+91 73143 44673) or email help@gulfudaan.com.",
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {enabled !== true ? null : (
        <>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-56 right-6 z-50 w-[92vw] max-w-sm h-[480px] bg-white rounded-2xl shadow-2xl border border-[#E5E1DA] flex flex-col overflow-hidden"
        >
          <div className="bg-[var(--brand-primary,#b1976b)] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div>
              <p className="font-heading text-base leading-tight">Gulfudaan Assistant</p>
              <p className="text-[11px] text-white/80">Powered by Gemini · online</p>
            </div>
            <button data-testid="chat-widget-close-btn" onClick={() => setOpen(false)} className="hover:opacity-70">
              <X size={18} />
            </button>
          </div>
          <div ref={scrollRef} data-testid="chat-widget-messages" className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FDFCFB]">
            {loadingHistory ? (
              <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-[#b1976b]" size={20} />
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "ml-auto bg-[var(--brand-primary,#b1976b)] text-white"
                      : "bg-white border border-[#E5E1DA] text-[#1A1A1A]"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:0.15s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:0.3s]" />
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="p-3 border-t border-[#E5E1DA] flex items-center gap-2 shrink-0">
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSend();
              }}
              placeholder="Ask about services, pricing..."
              className="flex-1 text-sm px-3 py-2 rounded-full border border-[#E5E1DA] focus:outline-none focus:border-[var(--brand-primary,#b1976b)]"
            />
            <button
              data-testid="chat-widget-send-btn"
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="w-9 h-9 rounded-full bg-[var(--brand-primary,#b1976b)] text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform shrink-0"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
      <button
        data-testid="chat-widget-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-40 right-6 z-50 w-14 h-14 rounded-full bg-[var(--brand-primary,#b1976b)] shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
      >
        {open ? <X className="text-white" size={24} /> : <MessageSquareText className="text-white" size={24} />}
      </button>
        </>
      )}
    </>
  );
}
